import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { UserX, UserPlus, RefreshCw, Send, CornerDownRight } from 'react-feather';
import { getRoaringPersonInfo } from '../../../../api/service/superAdmin/company-clients/service';
import HorizontalRule from '../../../../components/base/HorizontalRule';
import PagesHeader from '../../../../components/base/PagesHeader';
import { FormProps, Superviors } from '../types';
import { splitFullName } from '../../../../util/util';
import ErrorFeedback from '../../../../components/ErrorFeedback';
import classNames from 'classnames';
import { errorHighlighter, isPersonalNumberMatch } from '../../../../util/form';
import PersonalNumber from '../../../../components/form/inputs/PersonalNumber';
import useLocale from '../../../../hooks/useLocale';
import { DEFAULT_PERSON_INFO } from '../../../../types/general';
import TooltipBox from '../../../../components/base/Overlay/TooltipBox';

export default function Supervisors({ context }: FormProps) {
  const { supervisors, isEditMode, replaceSupervisorsInput, formGroup, appendDeletedSupervisor } = context;
  const { localize } = useLocale();
  const [, /*isPersonalNumberValid*/ setIsPersonalNumberValid] = useState<boolean>(true);

  const handleSupervisors = (e, _id) => {
    const _supervisors = [...supervisors];
    const foundDetail = _supervisors.find((i) => i['_id'] === _id);
    foundDetail![e.target.name] = e.target.value;
    replaceSupervisorsInput(_supervisors);
    setIsPersonalNumberValid(true);
  };

  const setPersonalInfo = (form, fullName) => {
    const { firstName, lastName } = splitFullName(fullName);
    const _supervisors = [...supervisors];
    const foundDetail = _supervisors.find((i) => i['_id'] === form['_id']);

    foundDetail!.fullName = fullName;
    foundDetail!.firstName = firstName;
    foundDetail!.lastName = lastName;

    replaceSupervisorsInput(_supervisors);
    setIsPersonalNumberValid(true);
  };

  const loadDetails = async (form: Superviors) => {
    try {
      setPersonalInfo(form, DEFAULT_PERSON_INFO.fullName);
      const { fullName } = await getRoaringPersonInfo(form?.personalNumber);
      setPersonalInfo(form, fullName);
      form['valid'] = true;
    } catch (e) {
      setIsPersonalNumberValid(false);
      form['valid'] = false;
    }
  };

  return (
    <div>
      <PagesHeader
        pageTitle={isEditMode ? localize('supervisor') : localize('supervisor') + ` (` + localize('at_least_one') + `)`}
        translate={false}
        headingClass="fs-h3 font-black"
      />
      <div className="form-section mb-3 dark-content-section">
        {supervisors.map((form, idx) => (
          <div key={form._id}>
            {idx > 0 && <HorizontalRule color="#C6C6C6" height="1px" />}
            <div className="cr-form-row">
              <PersonalNumber
                value={form?.personalNumber}
                onChangeHandler={(e) => handleSupervisors(e, form._id)}
                field={formGroup.getFieldMeta(`supervisors[${idx}].personalNumber`)}
                isValid={form['valid']}
                lazy={true}
              />
              <div>
                <Button
                  disabled={isPersonalNumberMatch(form?.personalNumber)}
                  variant="primary"
                  onClick={() => loadDetails(form)}
                  className="app-btn-secondary"
                >
                  {isEditMode ? (
                    <RefreshCw className="icon-gap-right" />
                  ) : (
                    <CornerDownRight className="icon-gap-right" />
                  )}{' '}
                  {isEditMode ? localize('refresh') : localize('fetch_name')}
                </Button>
              </div>
              <div className="first-name-field">
                <Form.Label className="fs-name mb-0 required">{localize('first_name')}</Form.Label>
                <Form.Control
                  size="lg"
                  type="text"
                  name="firstName"
                  value={form?.firstName}
                  disabled
                  required
                  className={classNames(errorHighlighter(formGroup.getFieldMeta(`supervisors[${idx}].firstName`)))}
                />
                <ErrorFeedback field={formGroup.getFieldMeta(`supervisors[${idx}].firstName`)} />
              </div>
              <div className="last-name-field">
                <Form.Label className="fs-name mb-0 required">{localize('last_name')}</Form.Label>
                <Form.Control
                  size="lg"
                  type="text"
                  name="lastName"
                  value={form?.lastName}
                  disabled
                  required
                  className={classNames(errorHighlighter(formGroup.getFieldMeta(`supervisors[${idx}].lastName`)))}
                />
                <ErrorFeedback field={formGroup.getFieldMeta(`supervisors[${idx}].lastName`)} />
              </div>
              <div className="email-field">
                <Form.Group>
                  <Form.Label className="fs-name mb-0 required">{`${localize('email_address')}`}</Form.Label>
                  <Form.Control
                    value={form?.email}
                    size="lg"
                    type="text"
                    onChange={(e) => handleSupervisors(e, form['_id'])}
                    name="email"
                    required
                    className={classNames(errorHighlighter(formGroup.getFieldMeta(`supervisors[${idx}].email`)))}
                  />
                  <ErrorFeedback field={formGroup.getFieldMeta(`supervisors[${idx}].email`)} />
                </Form.Group>
              </div>
              {isEditMode && (
                <div>
                  <Button className="app-btn-secondary">
                    <Send className="icon-gap-right" /> {localize('resend')} {localize('invite')}
                  </Button>
                </div>
              )}

              {idx >= 0 && (
                <div className="last-button-box">
                  <div className="button-align mx-3 mt-2">
                    <TooltipBox msg={`${localize('delete_user')}`}>
                      <UserX
                        onClick={() => {
                          const _supervisors = [...supervisors];
                          replaceSupervisorsInput(_supervisors.filter((i) => i['_id'] !== form['_id']));

                          if (_supervisors[idx]._id || _supervisors[idx].id) {
                            appendDeletedSupervisor(_supervisors[idx]._id || _supervisors[idx].id);
                          }
                        }}
                        className="cursor-pointer delete-icon className='mt-2'"
                      />
                    </TooltipBox>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        <Button
          onClick={() =>
            replaceSupervisorsInput([
              ...supervisors,
              { _id: `${Math.random()}`, personalNumber: '', email: '', fullName: '', firstName: '', lastName: '' },
            ])
          }
          className="app-btn-secondary"
        >
          <UserPlus className="theme-text" />
        </Button>
      </div>
    </div>
  );
}
